import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import UserListRow from 'components/UserListRow';
import UserListAdd from 'components/UserListAdd';

import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';

import Query, {
  UserListRefetchQuery,
} from '__generated__/UserListRefetchQuery.graphql';
import { UserList_data$key } from '__generated__/UserList_data.graphql';

type Props = {
  data: UserList_data$key;
};

const UserList: FC<Props> = props => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [data, { refetch, loading }] = useAvoidSuspense<
    UserListRefetchQuery,
    UserList_data$key
  >(
    graphql`
      fragment UserList_data on Query
      @refetchable(queryName: "UserListRefetchQuery") {
        users(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id
              ...UserListRow_user
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    Query
  );

  const connectionId = data.users?.__id;

  const [deleteUser, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = data.users ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<PeopleIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Vardas</TableHeadCell>

            <TableHeadCell center name="email">
              El. paštas
            </TableHeadCell>

            <TableHeadCell center name="canPublish">
              Gali publikuoti
            </TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <UserListRow key={node.id} user={node} onDelete={deleteUser} />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm" icon={<PersonAddIcon />}>
          <UserListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default UserList;
